!function() {
  'use strict';

  const SELECTORS = {
    rowsHolder: '[data-timeline-rows]',
    row: '[data-timeline-row]',
    rowEditor: '[data-timeline-row-editor]',
    button: '[data-timeline-button]',
    blockScroll: '[data-block-scroll]',
    video: '[data-timeline-video]',
  };

  const CLASSES = {
    selected: 'is-selected',
  };

  if (!customElements.get('timeline-component')) {
    customElements.define('timeline-component', class extends HTMLElement {
      constructor() {
        super();
        this.rows = this.querySelectorAll(SELECTORS.row);
        this.videos = this.querySelectorAll(`${SELECTORS.video} video`);
        this.selectedRow = null;
        this.hasMultipleRows = this.rows.length >= 2;

        if (this.hasMultipleRows) {
          this.rowsWidth = 0;
          this.rowsHeight = [];
          this.holderTop = this.getBoundingClientRect().top + window.scrollY;
          this.holderHeight = this.offsetHeight;
          this.buttons = this.querySelectorAll(SELECTORS.button);
          this.rowsHolder = this.querySelector(SELECTORS.rowsHolder);
          this.requestAnimation = null;
          this.isDesktopView = !theme.isMobile;
          this.isScrollEnabled = false;
          this.scrollEvent = (event) => this.scrollEvents(event);
          this.resizeEvent = () => this.resizeEvents();
        }
      }

      connectedCallback() {
        if (this.hasMultipleRows) {
          this.calculateRowsDimensions();
          this.requestAnimation = requestAnimationFrame(() => {
            this.calculatePosition();
            this.updateVideos();
          });

          if (this.isDesktopView && !this.isScrollEnabled) {
            this.isScrollEnabled = true;
            document.addEventListener('theme:scroll', this.scrollEvent);
          }

          this.rowsHolder.addEventListener('scroll', this.scrollEvent);
          document.addEventListener('theme:resize:width', this.resizeEvent);

          if (this.buttons.length) {
            this.buttons.forEach((button, index) => {
              button.addEventListener('click', (event) => {
                event.preventDefault();
                const row = event.currentTarget.closest(SELECTORS.row);
                if (!row) return;

                if (theme.isMobile) {
                  const offset = 16;
                  const left = row.offsetLeft - offset;
                  this.rowsHolder.scrollTo({ left, behavior: 'smooth' });
                } else {
                  const maxHeight = Math.max(...this.rowsHeight);
                  const rowHeight = this.holderHeight / this.rows.length;
                  const holderTop = this.getBoundingClientRect().top;
                  const scrollTo = (index > 0 && maxHeight < rowHeight ? rowHeight * index + holderTop : holderTop) + 1;
                  window.theme.scrollTo(scrollTo);
                }
              });
            });
          }
        } else {
          this.updateVideos();
        }
      }

      getActiveRow() {
        const selectedRows = [...this.rows].filter((row) => row.classList.contains(CLASSES.selected));
        return selectedRows[selectedRows.length - 1] || this.rows[0] || null;
      }

      updateVideos() {
        if (!this.videos.length) return;

        const activeRow = this.getActiveRow();
        if (activeRow === this.selectedRow) return;

        this.rows.forEach((row) => {
          const video = row.querySelector(`${SELECTORS.video} video`);
          if (!video) return;

          if (row === activeRow) {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {});
            }
          } else {
            video.pause();
          }
        });

        this.selectedRow = activeRow;
      }

      resizeEvents() {
        this.holderTop = this.getBoundingClientRect().top + window.scrollY;
        this.holderHeight = this.offsetHeight;
        this.calculateRowsDimensions();
        this.requestAnimation = requestAnimationFrame(() => {
          this.calculatePosition();
          this.updateVideos();
        });

        const isDesktop = !theme.isMobile;
        if (isDesktop && !this.isScrollEnabled) {
          this.isScrollEnabled = true;
          document.addEventListener('theme:scroll', this.scrollEvent);
        } else if (!isDesktop && this.isScrollEnabled) {
          this.isScrollEnabled = false;
          document.removeEventListener('theme:scroll', this.scrollEvent);
        }
      }

      scrollEvents(event) {
        if (!this.requestAnimation) {
          const target = event.currentTarget;
          this.requestAnimation = requestAnimationFrame(() => this.calculatePosition(target));
        }
      }

      removeAnimationFrame() {
        if (this.requestAnimation) {
          cancelAnimationFrame(this.requestAnimation);
          this.requestAnimation = null;
        }
      }

      calculateRowsDimensions() {
        if (!this.rows.length) return;

        this.rowsHeight = [];
        this.rowsWidth = 0;
        let mobileOffset = 0;

        this.rows.forEach((row, index) => {
          this.rowsHeight.push(row.offsetHeight);
          this.rowsWidth += row.offsetWidth + parseInt(getComputedStyle(row).marginRight);

          if (window.Shopify.designMode) {
            const editors = this.querySelectorAll(SELECTORS.rowEditor);
            if (editors.length) {
              mobileOffset += row.offsetHeight;
              editors[index]?.style.setProperty('--row-height-min', `${row.offsetHeight}px`);
              editors[index + 1]?.style.setProperty('--row-top-mobile', `${mobileOffset}px`);
            }
          }
        });
      }

      calculatePosition(scrollTarget = null) {
        this.removeAnimationFrame();
        this.holderTop = this.getBoundingClientRect().top + window.scrollY;

        const isDesktop = !theme.isMobile;
        const rowHeight = this.holderHeight / this.rows.length;
        let rowTop = this.holderTop;
        const viewportHeight = window.innerHeight;
        const scrollPosition = window.scrollY + 1.5 * viewportHeight;
        let mobilePercent = 0;

        if (!isDesktop && scrollTarget) {
          const scrollLeft = scrollTarget.scrollLeft - 16;
          const holderWidth = scrollTarget.offsetWidth;
          mobilePercent = scrollLeft / (this.rowsWidth - holderWidth) * 100;
          this.style.setProperty('--percent-mobile', `${mobilePercent}%`);
        }

        if (isDesktop && (scrollPosition < this.holderTop || scrollPosition > this.holderTop + this.holderHeight + viewportHeight)) {
          return;
        }

        this.rows.forEach((row, index) => {
          if (index > 0) {
            const rowPercent = row.offsetLeft / this.rowsWidth * 100;
            let isSelected = mobilePercent > rowPercent;

            if (isDesktop) {
              isSelected = rowTop + rowHeight < scrollPosition;
              if (rowTop - rowHeight < scrollPosition) {
                row.previousElementSibling?.style.setProperty('--percent-desktop', `${(scrollPosition - rowTop) / rowHeight * 100}%`);
              }
            }

            row.classList.toggle(CLASSES.selected, isSelected);
          }

          rowTop += rowHeight;
        });

        this.updateVideos();
      }

      disconnectedCallback() {
        if (this.isScrollEnabled) {
          this.isScrollEnabled = false;
          document.removeEventListener('theme:scroll', this.scrollEvent);
        }

        if (this.rowsHolder) {
          this.rowsHolder.removeEventListener('scroll', this.scrollEvent);
        }

        document.removeEventListener('theme:resize:width', this.resizeEvent);
        this.videos.forEach((video) => video.pause());
      }

      onBlockSelect(event) {
        const scrollContainer = this.querySelector(SELECTORS.blockScroll);
        if (!scrollContainer) return;

        const block = event.srcElement;
        const index = [...block.parentElement.children].indexOf(block);
        const row = this.rows[index];

        if (row) {
          scrollContainer.scrollTo({ top: 0, left: row.offsetLeft, behavior: 'smooth' });
        }
      }
    });
  }
}();
